import { useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const article = useMemo(() => (slug ? getArticleBySlug(slug) : undefined), [slug]);

  useEffect(() => {
    if (slug && !getArticleBySlug(slug)) {
      navigate('/', { replace: true });
      return;
    }
    window.scrollTo(0, 0);
  }, [slug, navigate]);

  const relatedArticles = useMemo(() => (slug ? getRelatedArticles(slug, 3) : []), [slug]);

  if (!article) return null;

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '亚洲免费在线',
      logo: {
        '@type': 'ImageObject',
        url: 'https://akeliusi.cn/logo.png',
      },
    },
    url: `https://akeliusi.cn/article/${article.slug}`,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://akeliusi.cn/article/${article.slug}`,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.categoryName, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <article className="max-w-4xl mx-auto px-4 md:px-6 py-8">
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Link
                  to={`/category/${article.category}`}
                  className="text-xs font-medium px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors whitespace-nowrap"
                >
                  {article.categoryName}
                </Link>
                <span className="text-sm text-foreground-500">{article.date}</span>
                <span className="text-sm text-foreground-500">&middot; {article.readTime}</span>
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 font-heading leading-tight mb-4">
                {article.title}
              </h1>
              <p className="text-base text-foreground-600 leading-relaxed">{article.description}</p>
              <div className="flex items-center gap-3 mt-4 pt-4 border-t border-background-200/70">
                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-user-line text-sm text-primary-600"></i>
                </div>
                <span className="text-sm text-foreground-700">{article.author}</span>
              </div>
            </header>

            <div className="mb-8 rounded-xl overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full aspect-[21/9] object-cover object-top"
                title={`${article.title} - 亚洲免费在线`}
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none
                prose-headings:font-heading prose-headings:text-foreground-950 prose-headings:font-bold
                prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
                prose-strong:text-foreground-900 prose-strong:font-semibold
                prose-a:text-primary-600 prose-a:no-underline hover:prose-a:text-primary-700
              "
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-8 pt-6 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs font-medium px-3 py-1.5 rounded-full bg-background-100 text-foreground-600"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="bg-background-100/50 py-12">
              <div className="max-w-4xl mx-auto px-4 md:px-6">
                <h3 className="text-xl font-semibold text-foreground-950 mb-6 font-heading">相关推荐</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {relatedArticles.map((ra) => (
                    <ArticleCard key={ra.slug} article={ra} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}